const CAP29 = {
  "id": 52,
  "title": "Los Bloques en una Blockchain",
  "slug": "los-bloques-en-una-blockchain",
  "type": "lesson",
  "category": "blockchain",
  "level": "basic",
  "sections": [
    {
      "heading": "¿Qué es un bloque?",
      "text": [
        "En una blockchain, un bloque es una unidad de información donde se agrupan transacciones y otros datos importantes. En lugar de validar cada transacción una por una, el sistema agrupa muchas transacciones dentro de un bloque para procesarlas juntas, lo que permite que el sistema sea más rápido, eficiente y seguro.",
        "Cada bloque se genera mediante minería y se valida usando un mecanismo llamado Proof of Work (PoW)."
      ]
    },
    {
      "heading": "Estructura básica de un bloque",
      "text": [
        "Cada bloque tiene un encabezado y un cuerpo:",
        "• Encabezado: contiene información técnica como: hash del bloque anterior, timestamp, nonce, Merkle Root.\n• Cuerpo: contiene todas las transacciones del bloque y la coinbase (recompensa para el minero).",
        "Con esta estructura, los bloques pueden verificarse públicamente y permanecer conectados entre sí formando una cadena."
      ],
      "image": {
        "src": "https://raw.githubusercontent.com/AppsDevsLeon/ImagesBooks/refs/heads/main/imagesFundamentosBlockchain/Biucoinmaster/Detalle-Bloque-Blockchain.jpg",
        "alt": "Estructura de un bloque con hash, previous hash, timestamp, nonce y transacciones.",
        "caption": "Estructura general de un bloque en la blockchain."
      }
    },
    {
      "heading": "Relación entre los bloques: PreviousHash",
      "text": [
        "Cada bloque está vinculado al anterior mediante un código llamado previousHash.",
        "Este código corresponde al hash del bloque previo, lo que significa que si alguien intentara modificar un bloque antiguo, todos los siguientes quedarían inválidos.",
        "Así es como la blockchain asegura que nadie pueda cambiar el historial de transacciones."
      ],
      "image": {
        "src": "https://raw.githubusercontent.com/AppsDevsLeon/ImagesBooks/refs/heads/main/imagesFundamentosBlockchain/Biucoinmaster/blockchain-struct.png",
        "alt": "Relación entre Block#0, Block#1 y Block#2 usando previousHash.",
        "caption": "Vinculación de los bloques mediante la referencia al hash del bloque anterior."
      }
    },
    {
      "heading": "Block Height o Altura de Bloque",
      "text": [
        "La altura de bloque (Block Height) es el número que identifica la posición de un bloque dentro de la cadena.",
        "El primer bloque de la historia se llama Bloque Génesis y tiene altura cero. A partir de ahí, cada bloque validado aumenta la altura en uno.",
        "Este número sirve para ubicar bloques, rastrear transacciones y medir el crecimiento de la red."
      ]
    },
    {
      "heading": "Importancia de la altura de bloque",
      "list": [
        "Permite localizar fácilmente un bloque en un explorador.",
        "Ayuda a saber cuántas confirmaciones tiene una transacción.",
        "Sirve para determinar bloqueos por tiempo (TimeLock) o bloqueos por altura.",
        "Indica el progreso y evolución de la blockchain."
      ]
    },
    {
      "heading": "Tipos de bloques",
      "text": ["En una blockchain pueden existir diferentes tipos de bloques:"],
      "list": [
        "Bloque Génesis: Primer bloque de la cadena (altura 0).",
        "Bloques Válidos: Bloques confirmados e incluidos oficialmente.",
        "Bloques Huérfanos (Orphan Blocks): Bloques válidos que no fueron aceptados por la red."
      ]
    },
    {
      "heading": "Bloques Génesis",
      "text": [
        "El Bloque Génesis es el primer bloque de una blockchain. Es único, no tiene previousHash, y permite iniciar la historia de todas las transacciones.",
        "Todos los nodos deben compartir el mismo Bloque Génesis para poder sincronizarse."
      ]
    },
    {
      "heading": "Bloques Válidos",
      "text": [
        "Son los bloques que han cumplido correctamente con las reglas de consenso y han sido añadidos oficialmente a la blockchain.",
        "En Bitcoin, cada bloque válido contiene alrededor de 2,000 transacciones.",
        "Cada bloque válido confirma las transacciones del bloque anterior, reforzando la seguridad."
      ]
    },
    {
      "heading": "Bloques Huérfanos",
      "text": [
        "Los bloques huérfanos son bloques que fueron minados correctamente pero no fueron elegidos por la red para formar parte de la cadena oficial.",
        "Esto ocurre cuando dos mineros generan bloques casi al mismo tiempo o cuando se intenta crear una bifurcación (fork)."
      ],
      "image": {
        "src": "https://raw.githubusercontent.com/AppsDevsLeon/ImagesBooks/refs/heads/main/imagesFundamentosBlockchain/Biucoinmaster/1e61beec-ddad-4b03-91ba-0972ab41e870.png",
        "alt": "Ejemplo de bifurcación y bloques huérfanos en Blockchain.com.",
        "caption": "Las bifurcaciones pueden generar bloques huérfanos que quedan fuera de la cadena principal."
      }
    },
    {
      "heading": "Ejemplo de tamaños y tiempos de bloques",
      "list": [
        "Bitcoin (BTC): bloques de 1MB cada 10 minutos.",
        "Bitcoin Cash (BCH): bloques de 8MB cada 10 minutos.",
        "Bitcoin Gold (BTG): bloques de 1MB cada 10 minutos.",
        "Bitcoin Private (BTCP): bloques de 2MB cada 2.5 minutos."
      ]
    },
    {
      "heading": "Resumen",
      "list": [
        "Los bloques son los contenedores de datos de la blockchain.",
        "Cada bloque contiene transacciones, hash, previousHash, timestamp y nonce.",
        "Los bloques se enlazan entre sí de forma segura e inmutable.",
        "Existen bloques válidos, génesis y huérfanos.",
        "La altura del bloque permite ubicar y confirmar transacciones."
      ]
    }
  ]
};

export default CAP29;
